import classNames from "classnames";
import { produce } from "immer";
import React, { type JSX, useState } from "react";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { NonFieldErrors } from "../../../forms/NonFieldErrors";
import { FORM_CLASS_NAME } from "../../../forms/classNames";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { format } from "../../../utils/format";
import { focusElement } from "../../../utils/keyboardFocus";
import { clamp } from "../../../utils/math";
import { CheckoutStep } from "../CheckoutStepContainer";
import { PaymentEnabledField } from "../PaymentEnabledField";
import { PaymentMethod } from "../PaymentMethod";
import { IFormErrors } from "../PaymentMethod.interfaces";
import {
    PRIMARY_PAYMENT_METHOD_KEY,
    SECONDARY_PAYMENT_METHOD_KEY,
    Step,
} from "../constants";
import { defaultMethodOrder } from "../defaults";
import {
    addPaymentMethod,
    removePaymentMethod,
    resetPaymentMethodsForm,
    resetPaymentStates,
    setFields,
    setPaymentMethodFields,
} from "../reducers";
import {
    IFinancingPayment,
    IPayment,
    IReduxAPIData,
    IReduxDerivedData,
    IReduxFormState,
} from "../reducers.interfaces";

const NAMES: { [t in IPayment["method_type"]]: string } = {
    "default": "",
    "cash": t`Cash`,
    "pay-later": t`Pay Later`,
    "cybersource-microform": t`Credit Card`,
    "bluefin": t`Secure Credit Card`,
    "financing": t`Financing`,
};

const getErrorCount = (errors: {
    [method_key: string]: IFormErrors;
}): number => {
    let sum = 0;
    for (const methodKey in errors) {
        for (const field in errors[methodKey]) {
            sum += errors[methodKey][field]!.length;
        }
    }
    return sum;
};

const getCreditCardPaymentType = (
    paymentMethods: IReduxAPIData["payment_methods"],
): "bluefin" | "cybersource-microform" => {
    return paymentMethods.includes("bluefin")
        ? "bluefin"
        : "cybersource-microform";
};

const buildMethodSelectionFields = (
    methodKey: string,
    dataPaymentMethods: IReduxAPIData["payment_methods"],
    formPaymentMethods: IReduxFormState["payment_methods"],
    derived: IReduxDerivedData,
    financingPlans: IReduxAPIData["financing_plans"],
    addPaymentMethodAction: (
        methodKey: string,
        methodType: IPayment["method_type"],
        amount?: string,
        useFullBalance?: boolean,
        financingAccount?: string,
    ) => void,
    removePaymentMethodAction: (methodKey: string) => void,
    hideErrorMessages: () => void,
): (JSX.Element | null)[] => {
    const selectedPrimaryMethod =
        formPaymentMethods[PRIMARY_PAYMENT_METHOD_KEY];
    const hasSecondaryMethod =
        SECONDARY_PAYMENT_METHOD_KEY in formPaymentMethods;
    const sortedMethods = [...dataPaymentMethods].sort((a, b) => {
        if (defaultMethodOrder.indexOf(a) < defaultMethodOrder.indexOf(b)) {
            return -1;
        }
        if (defaultMethodOrder.indexOf(a) > defaultMethodOrder.indexOf(b)) {
            return 1;
        }
        return 0;
    });
    const creditCardMethods = sortedMethods.filter(
        (m) => m === "bluefin" || m === "cybersource-microform",
    );
    return sortedMethods.flatMap((methodType) => {
        if (
            methodKey === SECONDARY_PAYMENT_METHOD_KEY &&
            (methodType === "cash" || methodType === "pay-later")
        ) {
            return [null];
        }
        // For split pay, determine which secondary CC method(s) to pair with
        let secondaryMethods: IPayment["method_type"][];
        if (methodKey === SECONDARY_PAYMENT_METHOD_KEY) {
            if (
                methodType === "cybersource-microform" ||
                methodType === "bluefin"
            ) {
                // CC + CC split: both slots use the same CC type
                secondaryMethods = [methodType];
            } else if (
                methodType === "financing" &&
                creditCardMethods.length > 0
            ) {
                // Financing + CC split: one entry per available CC type
                secondaryMethods = creditCardMethods;
            } else {
                secondaryMethods = [
                    getCreditCardPaymentType(dataPaymentMethods),
                ];
            }
        } else {
            secondaryMethods = [getCreditCardPaymentType(dataPaymentMethods)];
        }
        return secondaryMethods.map((secondary) => {
            let isSelected = false;
            if (methodKey === PRIMARY_PAYMENT_METHOD_KEY) {
                if (
                    !hasSecondaryMethod &&
                    selectedPrimaryMethod.method_type === methodType
                ) {
                    isSelected = true;
                }
            } else {
                if (
                    hasSecondaryMethod &&
                    selectedPrimaryMethod.method_type === methodType
                ) {
                    const selectedSecondaryMethod =
                        formPaymentMethods[SECONDARY_PAYMENT_METHOD_KEY];
                    isSelected =
                        selectedSecondaryMethod?.method_type === secondary;
                }
            }
            return (
                <PaymentEnabledField
                    key={`${methodKey}-${methodType}-${secondary}`}
                    primary={methodType}
                    secondary={secondary}
                    selected={isSelected}
                    methodKey={methodKey}
                    grandTotal={derived.grand_total}
                    plans={financingPlans}
                    addPaymentMethod={addPaymentMethodAction}
                    removePaymentMethod={removePaymentMethodAction}
                    onClick={hideErrorMessages}
                />
            );
        });
    });
};

const buildSelectedMethodSummary = (
    methodKey: string,
    formPaymentMethods: IReduxFormState["payment_methods"],
    dataPaymentMethods: IReduxAPIData["payment_methods"],
): (JSX.Element | null)[] | null => {
    const method = formPaymentMethods[methodKey];
    if (!method) {
        return null;
    }
    const summary = dataPaymentMethods.map((methodType) => {
        if (methodType !== method.method_type) {
            return null;
        }
        // TODO(i18n): Use t with interpolation to keep sentence together. Don't split translation into fragments. Example: {t`Paying via ${NAMES[methodType]}`}
        return (
            <p key={methodType}>
                {t`Paying via`} {NAMES[methodType]}
            </p>
        );
    });
    return summary;
};

export const PaymentMethods = (props: {
    heading: string;
    errors: string[];
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null;
    buildFortivaFinancingUpsell?: (grandTotal: string) => JSX.Element | null;
    enableSplitPay: boolean;
    onContinue: (event?: React.MouseEvent<HTMLElement>) => void;
    isCSR?: boolean;
    isCurrentStep?: boolean;
    formRef?: React.RefObject<HTMLFormElement | null>;
}): React.ReactNode => {
    const form = useAppSelector((state) => state.checkout.form);
    const data = useAppSelector((state) => state.checkout.data);
    const derived = useAppSelector((state) => state.checkout.derived);
    const gifts = useAppSelector((state) => state.checkout.gifts);
    const showForm = useAppSelector(
        (state) => state.checkout.form.current_step === Step.PAYMENT_METHODS,
    );
    const showSummary = useAppSelector(
        (state) => state.checkout.form.current_step > Step.PAYMENT_METHODS,
    );
    const dispatch = useAppDispatch();

    const [showErrorMessages, setShowErrorMessages] = useState(false);
    const [showMorePaymentOptions, setShowMorePaymentOptions] = useState(false);
    const [errors, setErrors] = useState<{
        [method_key: string]: IFormErrors;
    }>({
        [PRIMARY_PAYMENT_METHOD_KEY]: {},
        [SECONDARY_PAYMENT_METHOD_KEY]: {},
    });

    const hideErrorMessages = () => {
        setShowErrorMessages(false);
    };

    const onApplyDenial = () => {
        hideErrorMessages();
        dispatch(resetPaymentMethodsForm({ methods: data.payment_methods }));
        dispatch(resetPaymentStates());
    };

    const addPaymentMethodAction = (
        methodKey: string,
        methodType: IPayment["method_type"],
        amount?: string,
        useFullBalance?: boolean,
        financingAccount?: string,
    ) => {
        let method: IPayment;
        if (methodType === "financing") {
            method = {
                method_type: methodType,
                amount: amount || null,
                pay_balance: useFullBalance || false,
                new_financing_account: null,
                financing_account: financingAccount || "",
                financing_plan: null,
                has_agreed: false,
                has_esigned: false,
            };
        } else if (methodType === "bluefin") {
            method = {
                method_type: methodType,
                amount: amount || null,
                pay_balance: useFullBalance || false,
                payment_data: "",
            };
        } else if (methodType === "cybersource-microform") {
            method = {
                method_type: methodType,
                amount: amount || null,
                pay_balance: useFullBalance || false,
                transient_token: "",
                card_type: "",
                card_number: "",
            };
        } else if (methodType === "cash") {
            method = {
                method_type: methodType,
                amount: amount || null,
                pay_balance: useFullBalance || false,
            };
        } else if (methodType === "pay-later") {
            method = {
                method_type: methodType,
                amount: amount || null,
                pay_balance: useFullBalance || false,
            };
        } else {
            method = {
                method_type: methodType,
                amount: amount || null,
                pay_balance: useFullBalance || false,
            };
        }
        dispatch(
            addPaymentMethod({
                method_key: methodKey,
                method,
            }),
        );
    };

    const removePaymentMethodAction = (methodKey: string) => {
        dispatch(removePaymentMethod(methodKey));
    };

    const changeFormField = (fields: Partial<IReduxFormState>) => {
        dispatch(setFields(fields));
    };

    const changeAmount = (methodKey: string, newValue: string) => {
        const grandTotal = derived.grand_total;
        let value: string = newValue.replace(/[^0-9.]+/g, "");
        const n_value = Number(value);
        const n_grand_total = Number(grandTotal);
        let amount: number = clamp(n_value, 0, n_grand_total);
        if (isNaN(amount)) {
            amount = 0;
        }
        // We want to preserve `value` as a string in the case of trailing . or 0
        // but in the case of value being too high, we'll use the number version
        if (n_value > n_grand_total) {
            value = String(amount);
        }

        dispatch(
            setPaymentMethodFields({
                method_key: methodKey,
                fields: { amount: value },
            }),
        );

        // Update the other split pay amount
        const otherMethodKey =
            methodKey === PRIMARY_PAYMENT_METHOD_KEY
                ? SECONDARY_PAYMENT_METHOD_KEY
                : PRIMARY_PAYMENT_METHOD_KEY;
        dispatch(
            setPaymentMethodFields({
                method_key: otherMethodKey,
                fields: { amount: (n_grand_total - amount).toFixed(2) },
            }),
        );
    };

    const changeFinancingAccountNumber = (
        methodKey: string,
        accountNumber: string,
    ) => {
        // Stale payment state would disable the form on the first keystroke (#34256).
        if (data.payment_states) {
            dispatch(resetPaymentStates());
        }
        dispatch(
            setPaymentMethodFields({
                method_key: methodKey,
                fields: {
                    financing_account: accountNumber.replace(/[^0-9]+/g, ""),
                },
            }),
        );
    };

    const setPaymentMethodFieldsAction = (
        methodKey: string,
        fields: Partial<IPayment>,
    ) => {
        dispatch(
            setPaymentMethodFields({
                method_key: methodKey,
                fields,
            }),
        );
    };

    const resetPaymentMethodsAction = (
        methods: readonly IPayment["method_type"][],
    ) => {
        dispatch(resetPaymentMethodsForm({ methods }));
        dispatch(resetPaymentStates());
    };

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        if (getErrorCount(errors) > 0) {
            setShowErrorMessages(true);
            return;
        }
        props.onContinue();
    };

    const onValidStateChange = (
        fieldName: string,
        errorMessages: string[],
        methodKey: string,
    ) => {
        setErrors((oldErrs) => {
            // Validators report on every keystroke; skip the state update when
            // nothing changed so fast machine input (card readers) doesn't pay
            // for a re-render per character (#35361).
            const existing = oldErrs[methodKey]?.[fieldName] ?? [];
            if (
                existing.length === errorMessages.length &&
                existing.every((msg, i) => msg === errorMessages[i])
            ) {
                return oldErrs;
            }
            return produce(oldErrs, (draft) => {
                if (!draft[methodKey]) {
                    draft[methodKey] = {};
                }
                draft[methodKey][fieldName] = errorMessages;
            });
        });
    };

    const showMorePaymentOptionsHandler = () => {
        setShowMorePaymentOptions(true);
        // Make the active input not to lose the focus when more payment options are revealed
        focusElement(".checkout-step__payment-type--active input");
    };

    const addSplitPayToFinancing = () => {
        const payment = form.payment_methods[
            PRIMARY_PAYMENT_METHOD_KEY
        ] as IFinancingPayment;
        if (payment.new_financing_account) {
            const financingAmount =
                payment.new_financing_account.available_credit;
            // adding secondary, so split pay
            const creditAmount = derived.grand_total
                ? Number(derived.grand_total) - Number(financingAmount)
                : 0;
            // set primary method with max available amount
            dispatch(
                addPaymentMethod({
                    method_key: PRIMARY_PAYMENT_METHOD_KEY,
                    method: {
                        method_type: "financing",
                        amount: format.decimal(financingAmount),
                        pay_balance: false,
                        new_financing_account: payment.new_financing_account,
                        financing_account:
                            payment.new_financing_account.account_number,
                        financing_plan: null,
                        has_agreed: payment.has_agreed,
                        has_esigned: payment.has_esigned,
                    },
                }),
            );
            const creditCardType = getCreditCardPaymentType(
                data.payment_methods,
            );
            const creditCardMethod: IPayment =
                creditCardType === "cybersource-microform"
                    ? {
                          method_type: "cybersource-microform",
                          amount: format.decimal(creditAmount),
                          pay_balance: false,
                          transient_token: "",
                          card_type: "",
                          card_number: "",
                      }
                    : {
                          method_type: "bluefin",
                          amount: format.decimal(creditAmount),
                          pay_balance: false,
                          payment_data: "",
                      };
            dispatch(
                addPaymentMethod({
                    method_key: SECONDARY_PAYMENT_METHOD_KEY,
                    method: creditCardMethod,
                }),
            );

            setShowMorePaymentOptions(true);
            hideErrorMessages();
        }
    };

    const buildSelectedMethodForm = () => {
        return (
            <PaymentMethod
                form={form}
                data={data}
                derived={derived}
                gifts={gifts}
                errors={errors}
                onValidStateChange={onValidStateChange}
                showErrorMessages={showErrorMessages}
                onApplyDenial={onApplyDenial}
                onSubmit={onSubmit}
                buildFinancingUpsell={props.buildFinancingUpsell}
                buildFortivaFinancingUpsell={props.buildFortivaFinancingUpsell}
                changeFormField={changeFormField}
                changeAmount={changeAmount}
                changeFinancingAccountNumber={changeFinancingAccountNumber}
                setPaymentMethodFields={setPaymentMethodFieldsAction}
                resetPaymentMethods={resetPaymentMethodsAction}
                addSplitPayToFinancing={addSplitPayToFinancing}
                enableSplitPay={props.enableSplitPay}
                isCSR={!!props.isCSR}
                formRef={props.formRef}
            />
        );
    };

    const bodyClasses = classNames({
        "checkout-step__body": true,
        "checkout-step__body--collapsed": !showForm,
    });
    const summaryClasses = classNames({
        "checkout-step__summary": true,
        "checkout-step__summary--collapsed": !showSummary,
    });
    const showMoreClasses = classNames({
        "checkout-step__summary--collapsed": showMorePaymentOptions,
    });
    const splitOptionsClasses = classNames({
        "checkout-step__summary--collapsed": !showMorePaymentOptions,
    });
    return (
        <CheckoutStep
            className={concatClassNames([
                "checkout-step--payment-method",
                FORM_CLASS_NAME,
            ])}
            heading={props.heading}
            isCurrentStep={props.isCurrentStep}
        >
            <div className={bodyClasses}>
                <NonFieldErrors
                    errors={props.errors}
                    showErrorMessages={true}
                />
                <fieldset>
                    <legend className="ada-screenreader-only">
                        {props.heading}
                    </legend>
                    <div className="checkout-step__payment-enabled-fields">
                        <ul>
                            {buildMethodSelectionFields(
                                PRIMARY_PAYMENT_METHOD_KEY,
                                data.payment_methods,
                                form.payment_methods,
                                derived,
                                data.financing_plans,
                                addPaymentMethodAction,
                                removePaymentMethodAction,
                                hideErrorMessages,
                            )}
                        </ul>
                    </div>
                    {props.enableSplitPay && (
                        <div>
                            <button
                                className={
                                    showMoreClasses ||
                                    "checkout-step__split-pay--toggle"
                                }
                                onClick={showMorePaymentOptionsHandler}
                            >
                                {richT(t`Show more payment options <arrow />`, {
                                    arrow: () => <span>{"\u25bc"}</span>,
                                })}
                            </button>
                            <div className={splitOptionsClasses}>
                                <div className="checkout-step__payment-enabled-fields">
                                    <ul>
                                        {buildMethodSelectionFields(
                                            SECONDARY_PAYMENT_METHOD_KEY,
                                            data.payment_methods,
                                            form.payment_methods,
                                            derived,
                                            data.financing_plans,
                                            addPaymentMethodAction,
                                            removePaymentMethodAction,
                                            hideErrorMessages,
                                        )}
                                    </ul>
                                </div>
                                <div>
                                    {/* TODO(i18n): Use jt to keep sentence together with interpolated component. Don't split translation into fragments. Example: {jt`${<strong>Please Note:</strong>} The billing address you provided during checkout must match the address on file with both payment methods.`} */}
                                    <p>
                                        <strong>{t`Please Note:`}</strong>{" "}
                                        {t`The billing address you provided during checkout must match the address on file with both payment methods.`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </fieldset>

                {buildSelectedMethodForm()}
            </div>
            <div className={summaryClasses}>
                {buildSelectedMethodSummary(
                    PRIMARY_PAYMENT_METHOD_KEY,
                    form.payment_methods,
                    data.payment_methods,
                )}
                {buildSelectedMethodSummary(
                    SECONDARY_PAYMENT_METHOD_KEY,
                    form.payment_methods,
                    data.payment_methods,
                )}
                <LoadingSpinner />
            </div>
        </CheckoutStep>
    );
};
